import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SkillCostAggregate } from "@/lib/storyRefs";
import { formatCost } from "@/lib/format";

interface SkillCostChartProps {
  skillCosts: SkillCostAggregate[];
  settledCount: number;
}

/**
 * Horizontal bar chart of cost aggregated by skill across the fan-out (D12).
 * Hidden when no skill data is available.
 */
export function SkillCostChart({
  skillCosts,
  settledCount,
}: SkillCostChartProps) {
  const { t } = useTranslation();

  if (skillCosts.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("projectPortal.metrics.skillChart.title")}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t("projectPortal.metrics.skillChart.caption", {
            count: settledCount,
          })}
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(120, skillCosts.length * 40)}>
          <BarChart
            data={skillCosts}
            layout="vertical"
            margin={{ left: 80 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-border"
            />
            <XAxis
              type="number"
              tick={{ fontSize: 11 }}
              tickFormatter={formatCost}
            />
            <YAxis
              type="category"
              dataKey="skill"
              tick={{ fontSize: 11 }}
              width={75}
            />
            <Tooltip formatter={(val: number) => formatCost(val)} />
            <Bar
              dataKey="cost_usd"
              fill="hsl(222.2 47.4% 11.2%)"
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
